import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name?.split(" ")[0] || "seeker"}`);
      const from = loc.state?.from || (res.user.role === "admin" ? "/admin" : "/dashboard");
      nav(from);
    } else {
      setErr(res.error);
    }
  };

  return (
    <div className="min-h-[90vh] flex items-center justify-center px-6 py-16 relative overflow-hidden">
      <div className="absolute inset-0 starfield opacity-40 pointer-events-none" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-8">
          <Sparkles className="text-[#D4AF37]" size={20} strokeWidth={1.5} />
          <span className="font-serif text-2xl">Vedacosmos</span>
        </Link>

        <div className="bg-[#0A0C10] border border-white/10 rounded-2xl p-8 backdrop-blur-xl">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Welcome back</div>
          <h1 className="font-serif text-3xl mb-6">Sign in to your journey</h1>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none text-[#EAE6DF] transition"
                placeholder="you@vedacosmos.com"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none text-[#EAE6DF] transition"
                placeholder="••••••••"
              />
            </div>
            {err && <div data-testid="login-error" className="text-sm text-[#FF5A36]">{err}</div>}
            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={busy}
              className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {busy ? "Signing in..." : "Sign In"} <ArrowRight size={16} />
            </button>
          </form>

          <div className="text-sm text-[#A0A4AB] text-center mt-6">
            New here?{" "}
            <Link to="/register" className="text-[#D4AF37] hover:text-[#E5C865]">Create an account</Link>
          </div>
          <div className="text-sm text-[#A0A4AB] text-center mt-3">
            or <Link to="/otp-login" data-testid="otp-login-link" className="text-emerald-400 hover:text-emerald-300">Sign in via OTP / WhatsApp</Link>
          </div>
          <div className="text-sm text-[#A0A4AB] text-center mt-3">
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-[#A0A4AB] hover:text-[#D4AF37] text-xs">
              Forgot password?
            </Link>
          </div>
        </div>

        <div className="mt-6 p-4 rounded-lg bg-[#0A0C10]/70 border border-white/5 text-xs text-[#6E737D]">
          <div className="uppercase tracking-widest text-[#D4AF37]/70 mb-2">Demo Access</div>
          <div>Admin: admin@vedacosmos.com / Admin@Veda2026</div>
          <div>Astrologer: rajesh@vedacosmos.com / Provider@2026</div>
        </div>
      </div>
    </div>
  );
}
